<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    nextTick,
    onBeforeUnmount,
    ref,
    watch,
  } from 'vue';
  import { useEventListener, useNow } from '@vueuse/core';
  import type { IUpdateReservationSettingsDto } from '@/api/services/reservations.service';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import { registerReservationBranchLeave } from '@/composition/reservations/reservation-branch-leave';
  import { provideReservationGuide } from '@/composition/reservations/reservation-guide-context';
  import type {
    IGuideStepHandle,
    IReservationGuideAlertsDraft,
    IReservationGuideContext,
    IReservationGuideExitTarget,
    IReservationGuideScope,
  } from '@/composition/reservations/reservation-guide-context';
  import { useReservationFloorPlan } from '@/composition/reservations/use-reservation-floor-plan';
  import { useReservationTablesDraft } from '@/composition/reservations/use-reservation-tables-draft';
  import { useReservationsSettings } from '@/composition/reservations/use-reservations-settings';
  import { markReservationGuideShown } from '@/composition/reservations/use-reservation-guide-auto-open';
  import { useAuthStore } from '@/composition/use-auth';
  import { useModal } from '@/composition/use-modal';
  import { useStableWindowSize } from '@/composition/use-stable-window-size';
  import { useUi } from '@/composition/use-ui';
  import {
    guidePreviewFocus,
    nextGuideScreen,
    previousGuideScreen,
    reservationGuideFinishState,
    resumeReservationGuide,
    usesReservationVoseo,
  } from '@/domain/reservations/reservation-guide';
  import type { TReservationGuideScreen } from '@/domain/reservations/reservation-guide';
  import { buildGuestPreview } from '@/domain/reservations/reservation-guest-preview';
  import type { IGuestPreviewModel } from '@/domain/reservations/reservation-guest-preview';
  import { isReservationTableWithoutSeats } from '@/domain/reservations/reservation-setup';
  import {
    DEFAULT_TABLE_SEATS,
    seedTablesDraft,
  } from '@/domain/reservations/reservation-tables-draft';
  import { getReservationTimeZone } from '@/domain/reservations/reservation-time';
  import { PermissionType } from '@/enums/PermissionType';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import V2ReservationsGuideBar from './V2ReservationsGuideBar.vue';
  import V2ReservationsGuideFinish from './V2ReservationsGuideFinish.vue';
  import V2ReservationsGuidePreview from './V2ReservationsGuidePreview.vue';
  import V2ReservationsGuideStepAlerts from './V2ReservationsGuideStepAlerts.vue';
  import V2ReservationsGuideStepGroups from './V2ReservationsGuideStepGroups.vue';
  import V2ReservationsGuideStepHours from './V2ReservationsGuideStepHours.vue';
  import V2ReservationsGuideStepTables from './V2ReservationsGuideStepTables.vue';
  import V2ReservationsGuideTopBar from './V2ReservationsGuideTopBar.vue';
  import { guideDurationLabel } from './reservation-guide-copy';

  /**
   * The step-by-step setup (§8.1): top bar · scroll body (the active step and
   * the guest preview) · bottom bar. Owns every piece of state and provides it
   * through the guide context; steps emit nothing upwards. The active step's
   * template ref is read as an `IGuideStepHandle`.
   */
  const WIDE_MIN_WIDTH = 1024;
  const emit = defineEmits<{
    (e: 'exit', target?: IReservationGuideExitTarget): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const restaurant = useRestaurantStore();
  const auth = useAuthStore();
  const modals = useModal();
  const ui = useUi();
  const { width } = useStableWindowSize();
  const now = useNow({ interval: 60_000 });
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const tc = (key: string, count: number, params?: Record<string, unknown>) =>
    String(proxy.$tc(key, count, params));

  const scope = (): IReservationGuideScope | null => {
    const restaurantId = restaurant.restaurant?.id;
    const branchId = restaurant.selectedBranch?.id;
    return restaurantId && branchId ? { restaurantId, branchId } : null;
  };
  const scopeKey = computed(() => {
    const current = scope();
    return current ? `${current.restaurantId}:${current.branchId}` : '';
  });
  const settings = useReservationsSettings();
  const draft = useReservationTablesDraft(() => settings.configuration.value, {
    onError: (cause) => ui.serverError(cause, tr('reservations.tables.error')),
  });
  const floorPlan = useReservationFloorPlan(scope, () => draft.tables.value);
  const screen = ref<TReservationGuideScreen>('hours');
  let returnTo: 'done' | null = null;
  const excludedIds = ref<number[]>([]);
  const seatlessOnOpen = ref(false);
  const allSeats = ref(DEFAULT_TABLE_SEATS);
  const emptyAlerts = (): IReservationGuideAlertsDraft => ({
    email: '',
    tel: '',
    whatsapp: '',
  });
  const alertsDraft = ref<IReservationGuideAlertsDraft>(emptyAlerts());
  const alertsPrefill = ref<IReservationGuideAlertsDraft>(emptyAlerts());
  const rulesEditing = ref(false);
  const pending = ref(0);
  const busy = computed(() => pending.value > 0);
  const activeStep = ref<IGuideStepHandle | null>(null);
  const scrollBody = ref<HTMLElement | null>(null);
  let alive = true;
  /** The scope whose bundle already chose the landing screen. */
  let resolvedScope: string | null = null;
  let planLoadedFor: string | null = null;
  let tablesSeededFor: string | null = null;
  const inflight = new Set<Promise<unknown>>();
  const IDLE_WAIT_MS = 15_000;
  /** The bottom CTA spins only for the write it started, not for every guide write. */
  const nextBusy = ref(false);
  let leaveConfirmation: Promise<boolean> | null = null;

  const configuration = computed(() => settings.configuration.value);
  const status = computed(() => settings.status.value);
  const canManage = computed(() =>
    auth.permissionSet.has(PermissionType.ReservationsManage)
  );
  const wide = computed(() => width.value >= WIDE_MIN_WIDTH);
  const finishState = computed(() =>
    reservationGuideFinishState(
      configuration.value?.settings ?? { enabled: false, onlineEnabled: false }
    )
  );
  // D23: once the live finish screen was seen here, the automatic landing is spent.
  watch(
    () =>
      screen.value === 'done' && finishState.value !== 'ready'
        ? scopeKey.value
        : '',
    (key) => {
      if (key) markReservationGuideShown(key);
    }
  );
  const branchName = computed(() =>
    (restaurant.visibleBranches?.length ?? 0) > 1
      ? restaurant.selectedBranch?.name ?? null
      : null
  );
  const alertsDirty = computed(() =>
    (['email', 'tel', 'whatsapp'] as const).some(
      (field) =>
        alertsDraft.value[field].trim() !== alertsPrefill.value[field].trim()
    )
  );
  const hasUnsaved = computed(
    () => draft.dirty.value || alertsDirty.value || rulesEditing.value
  );

  //#region Writes
  /** pending++ / current() / serverError / pending--; null when the task failed or the scope changed. */
  function run<T>(
    task: () => Promise<T>,
    fallbackMessage: string
  ): Promise<T | null> {
    const requestKey = scopeKey.value;
    const current = () => alive && scopeKey.value === requestKey;
    pending.value++;
    const promise = (async (): Promise<T | null> => {
      try {
        const result = await task();
        return current() ? result : null;
      } catch (cause) {
        if (current()) ui.serverError(cause, fallbackMessage);
        return null;
      } finally {
        pending.value--;
      }
    })();
    // Never rejects (everything is caught above), so both branches only bookkeep.
    inflight.add(promise);
    const settle = () => inflight.delete(promise);
    promise.then(settle, settle);
    return promise;
  }
  async function reload(): Promise<boolean> {
    return (
      (await run(
        () => settings.load(),
        tr('reservations.settings.loadError')
      )) !== null
    );
  }
  async function savePatch(
    patch: IUpdateReservationSettingsDto
  ): Promise<boolean> {
    const saved = await run(
      () => settings.savePatch(patch),
      tr('reservations.settings.saveError')
    );
    return saved !== null;
  }
  /** Guards evaluate what is unsaved only once every write has landed. */
  /** Resolves once no guide write is in flight, or after a bounded wait when one hangs. */
  async function awaitIdle(): Promise<void> {
    if (!inflight.size) return;
    const deadline = new Promise<false>((resolve) =>
      setTimeout(() => resolve(false), IDLE_WAIT_MS)
    );
    while (inflight.size) {
      const settled = await Promise.race([
        Promise.all([...inflight]).then(() => true),
        deadline,
      ]);
      // Past the deadline the guard judges what is unsaved with the write still pending.
      if (!settled) return;
    }
  }
  //#endregion

  //#region Screens
  function blurActive() {
    const active = document.activeElement;
    if (active instanceof HTMLElement) active.blur();
  }
  /** Every screen change: the Reglas guard, blur, then the swap and a scroll to the top. */
  async function transition(
    target: TReservationGuideScreen,
    rememberReturn: boolean
  ) {
    if (rulesEditing.value && target !== 'done') {
      if (!(await confirmLeave()) || !alive) return;
      rulesEditing.value = false;
    }
    blurActive();
    await nextTick();
    if (!alive) return;
    if (rememberReturn) {
      if (screen.value === 'done' && target !== 'done') returnTo = 'done';
    } else returnTo = null;
    screen.value = target;
    scrollBody.value?.scrollTo({ top: 0 });
  }
  function go(target: TReservationGuideScreen) {
    void transition(target, true);
  }
  function next() {
    if (returnTo) {
      returnTo = null;
      void transition('done', false);
    } else void transition(nextGuideScreen(screen.value), false);
  }
  function back() {
    returnTo = null;
    const previous = previousGuideScreen(screen.value);
    if (previous) void transition(previous, false);
  }
  /** First show of Mesas / Grupos in a scope: the plan loads; Mesas also seeds the draft (D12). */
  function onScreenShown(value: TReservationGuideScreen) {
    const key = scopeKey.value;
    const cfg = configuration.value;
    if (!key || !cfg) return;
    if ((value === 'tables' || value === 'groups') && planLoadedFor !== key) {
      planLoadedFor = key;
      void floorPlan.load();
    }
    if (value === 'tables' && tablesSeededFor !== key) {
      tablesSeededFor = key;
      const seed = seedTablesDraft(cfg.tables, DEFAULT_TABLE_SEATS);
      // The note only makes sense when the seed actually put seats somewhere
      // (heuristic entries only switch counters off).
      seatlessOnOpen.value = Object.values(seed.values).some(
        (entry) => entry.reservationsMode === 'online'
      );
      draft.seed(seed.values);
      excludedIds.value = seed.excludedIds;
    }
  }
  function clearExcluded(ids?: readonly number[]) {
    excludedIds.value = ids
      ? excludedIds.value.filter((id) => !ids.includes(id))
      : [];
  }
  function commitAlerts(fields?: Partial<IReservationGuideAlertsDraft>) {
    alertsPrefill.value = fields
      ? { ...alertsPrefill.value, ...fields }
      : { ...alertsDraft.value };
  }
  //#endregion

  //#region Leave guards
  /** True at once when nothing is unsaved; one dialog at a time otherwise. */
  function confirmLeave(): Promise<boolean> {
    if (!hasUnsaved.value) return Promise.resolve(true);
    if (!leaveConfirmation)
      leaveConfirmation = modals
        .confirm({
          title: tr('reservations.guide.leaveTitle'),
          message: tr('reservations.guide.leaveMessage'),
          acceptText: tr('reservations.guide.leaveDiscard'),
          cancelText: tr('cancel'),
          acceptColor: 'error',
        })
        .finally(() => {
          leaveConfirmation = null;
        });
    return leaveConfirmation;
  }
  async function guardedLeave(): Promise<boolean> {
    await awaitIdle();
    if (!alive) return true;
    return confirmLeave();
  }
  async function exit(target?: IReservationGuideExitTarget) {
    if (!(await guardedLeave()) || !alive) return;
    // Nothing unsaved is left behind, so the page's own navigation passes the guard.
    draft.discard();
    rulesEditing.value = false;
    alertsDraft.value = { ...alertsPrefill.value };
    emit('exit', target);
  }
  const removeBranchGuard = registerReservationBranchLeave(guardedLeave);
  // This is not a routed component: beforeRouteLeave would never run.
  const removeRouteGuard = proxy.$router.beforeEach((to, from, nextRoute) => {
    if (to.fullPath === from.fullPath) {
      nextRoute();
      return;
    }
    void guardedLeave()
      .then((confirmed) => (confirmed ? nextRoute() : nextRoute(false)))
      .catch(() => nextRoute(false));
  });
  useEventListener(window, 'beforeunload', (event: BeforeUnloadEvent) => {
    if (!hasUnsaved.value) return;
    event.preventDefault();
    event.returnValue = '';
  });
  //#endregion

  //#region Preview
  const focus = computed(() =>
    guidePreviewFocus(screen.value, finishState.value)
  );
  const voseo = computed(() =>
    usesReservationVoseo(proxy.$i18n.locale, restaurant.restaurant?.country)
  );
  const restaurantName = computed(() => restaurant.restaurant?.name ?? '');
  const previewModel = computed<IGuestPreviewModel | null>(() => {
    const cfg = configuration.value;
    if (!cfg) return null;
    const branch = restaurant.selectedBranch;
    return buildGuestPreview({
      now: now.value,
      timeZone: getReservationTimeZone(
        branch?.location,
        restaurant.restaurant?.country
      ),
      businessDayStartHour: branch?.businessDayStartHour,
      shifts: [...cfg.shifts, ...(activeStep.value?.previewShifts ?? [])],
      settings: cfg.settings,
      tables: draft.tables.value,
      groups: cfg.tableGroups,
      contact: {
        email: alertsDraft.value.email.trim() || null,
        tel: alertsDraft.value.tel.trim() || null,
        whatsapp: alertsDraft.value.whatsapp.trim() || null,
      },
    });
  });
  const previewCaption = computed(() => {
    const model = previewModel.value;
    const cfg = configuration.value;
    if (!model || !cfg) return '';
    switch (focus.value) {
      case 'time':
        // Shifts with times, even when none can still be booked this week
        // (the replica then shows «Sin horarios»): the caption names them.
        return model.offersTimes
          ? tr('reservations.guide.hours.previewCaption', {
              duration: guideDurationLabel(cfg.settings.defaultDurationMin, tr),
            })
          : tr('reservations.preview.noShifts');
      case 'party': {
        // No maximum while nobody can book: «0 mesas se reservan online».
        const party =
          model.onlineTables === 0
            ? `0 ${tc('reservations.tables.editor.counterTables', 0)}`
            : tc(
                'reservations.guide.preview.captionParty',
                model.onlineTables,
                { tables: model.onlineTables, max: model.maxParty }
              );
        return screen.value === 'groups'
          ? `${party} ${tc(
              'reservations.guide.preview.captionGroups',
              model.groups,
              { count: model.groups }
            )}`
          : party;
      }
      case 'contact':
        return model.contact.email
          ? tr('reservations.guide.alerts.previewCaption', {
              email: model.contact.email,
            })
          : tr('reservations.guide.alerts.previewNoEmail');
      case 'live':
        return tr('reservations.guide.preview.captionLive');
      default:
        return tr('reservations.guide.preview.captionOff');
    }
  });
  /** Amber while some party sizes the owner allows online have no table yet. */
  const previewCaptionTone = computed<'warning' | undefined>(() => {
    const model = previewModel.value;
    const cfg = configuration.value;
    return focus.value === 'party' &&
      !!model &&
      !!cfg &&
      model.bookable > 0 &&
      model.bookable < cfg.settings.maxPartyOnline
      ? 'warning'
      : undefined;
  });
  /** The inline header's one-liner; before any table exists it quotes the seats being created. */
  const previewSummary = computed(() => {
    const cfg = configuration.value;
    if (!cfg) return '';
    return screen.value === 'tables' && cfg.tables.length === 0
      ? tr('reservations.guide.tables.previewSummary', {
          max: Math.min(cfg.settings.maxPartyOnline, allSeats.value),
        })
      : previewCaption.value;
  });
  const previewDefaultOpen = computed(() => screen.value === 'done');
  //#endregion

  //#region Bottom bar (§8.1)
  interface IBarState {
    backable: boolean;
    nextLabel: string | null;
    nextVariant: 'primary' | 'outline';
    nextDisabled: boolean;
    secondaryLabel: string | null;
  }
  const bar = computed<IBarState>(() => {
    const step = activeStep.value;
    const stepDisabled = !(step?.ready ?? false) || busy.value;
    const nextLabel = step?.nextLabel ?? tr('reservations.guide.next');
    switch (screen.value) {
      case 'hours':
      case 'tables':
      case 'alerts':
        return {
          backable: screen.value !== 'hours',
          nextLabel,
          nextVariant: 'primary',
          nextDisabled: stepDisabled,
          secondaryLabel: null,
        };
      case 'groups':
        return {
          backable: true,
          nextLabel,
          nextVariant: 'primary',
          nextDisabled: stepDisabled,
          secondaryLabel: step?.secondaryLabel ?? null,
        };
      default:
        return finishState.value === 'ready'
          ? {
              backable: true,
              nextLabel: null,
              nextVariant: 'primary',
              nextDisabled: true,
              secondaryLabel: tr('reservations.guide.finish.goSettings'),
            }
          : {
              backable: false,
              nextLabel: canManage.value
                ? tr('reservations.guide.finish.goToday')
                : null,
              nextVariant: 'outline',
              nextDisabled: busy.value,
              secondaryLabel: tr('reservations.guide.finish.goSettings'),
            };
    }
  });
  async function onNext() {
    if (nextBusy.value) return;
    nextBusy.value = true;
    try {
      if (screen.value === 'done') await exit({ tab: 'today' });
      else await activeStep.value?.continue();
    } finally {
      if (alive) nextBusy.value = false;
    }
  }
  function onSecondary() {
    if (screen.value === 'done') void exit({ tab: 'settings' });
    else next();
  }
  //#endregion

  const context: IReservationGuideContext = {
    scope,
    configuration: settings.configuration,
    status: settings.status,
    screen,
    finishState,
    tablesDraft: draft,
    floorPlan,
    excludedIds,
    clearExcluded,
    seatlessOnOpen,
    allSeats,
    alertsDraft,
    alertsPrefill,
    commitAlerts,
    rulesEditing,
    busy,
    run,
    reload,
    savePatch,
    go,
    next,
    back,
    exit,
  };
  provideReservationGuide(context);

  // The first bundle of a scope decides where the guide lands (D16) and prefills Avisos.
  watch(
    configuration,
    (cfg) => {
      if (!cfg || resolvedScope === scopeKey.value) return;
      resolvedScope = scopeKey.value;
      screen.value = resumeReservationGuide(cfg);
      returnTo = null;
      const branch = restaurant.selectedBranch;
      alertsPrefill.value = {
        email: cfg.settings.staffEmail ?? auth.user?.email ?? '',
        tel: branch?.tel ?? '',
        whatsapp: branch?.whatsapp ?? '',
      };
      alertsDraft.value = { ...alertsPrefill.value };
      onScreenShown(screen.value);
    },
    { immediate: true }
  );
  watch(screen, onScreenShown);
  watch(scopeKey, () => {
    resolvedScope = null;
    planLoadedFor = null;
    tablesSeededFor = null;
    screen.value = 'hours';
    returnTo = null;
    excludedIds.value = [];
    seatlessOnOpen.value = false;
    allSeats.value = DEFAULT_TABLE_SEATS;
    rulesEditing.value = false;
    alertsPrefill.value = emptyAlerts();
    alertsDraft.value = emptyAlerts();
    draft.discard();
  });
  onBeforeUnmount(() => {
    alive = false;
    removeRouteGuard();
    removeBranchGuard();
  });
</script>

<template>
  <div class="reservations-guide">
    <V2ReservationsGuideTopBar
      :screen="screen"
      :branch-name="branchName ?? undefined"
      :disabled="busy"
      @exit="exit()"
    />
    <div ref="scrollBody" class="guide-scroll" :class="{ wide }">
      <div
        v-if="!configuration && status === 'loading'"
        class="guide-loading"
        aria-busy="true"
      >
        <PosUiSkeleton class="skeleton-title" />
        <PosUiSkeleton v-for="index in 3" :key="index" class="skeleton-row" />
      </div>
      <PosUiEmptyState
        v-else-if="!configuration && status === 'error'"
        icon="alert-circle-outline"
        :title="$t('reservations.settings.loadError')"
      >
        <template #action
          ><PosUiButton
            color="neutral"
            variant="outline"
            icon="refresh"
            :disabled="busy"
            @click="reload"
            >{{ $t('reservations.page.retry') }}</PosUiButton
          ></template
        >
      </PosUiEmptyState>
      <div v-else-if="configuration && previewModel" class="guide-content">
        <section class="guide-step">
          <V2ReservationsGuideStepHours
            v-if="screen === 'hours'"
            ref="activeStep"
          />
          <V2ReservationsGuideStepTables
            v-else-if="screen === 'tables'"
            ref="activeStep"
          />
          <V2ReservationsGuideStepGroups
            v-else-if="screen === 'groups'"
            ref="activeStep"
          />
          <V2ReservationsGuideStepAlerts
            v-else-if="screen === 'alerts'"
            ref="activeStep"
          />
          <V2ReservationsGuideFinish v-else />
        </section>
        <!-- One DOM place: the wide tier moves it beside the step with CSS only. -->
        <V2ReservationsGuidePreview
          class="guide-preview"
          :model="previewModel"
          :focus="focus"
          :layout="wide ? 'aside' : 'inline'"
          :caption="previewCaption"
          :caption-tone="previewCaptionTone"
          :summary="previewSummary"
          :screen="screen"
          :default-open="previewDefaultOpen"
          :restaurant-name="restaurantName"
          :voseo="voseo"
        />
      </div>
      <PosUiEmptyState
        v-else
        icon="store-outline"
        :title="$t('branch')"
        :description="$t('reservations.settings.noBranch')"
      />
    </div>
    <V2ReservationsGuideBar
      v-if="configuration"
      :backable="bar.backable"
      :next-label="bar.nextLabel ?? undefined"
      :next-variant="bar.nextVariant"
      :next-disabled="bar.nextDisabled"
      :next-loading="nextBusy"
      :busy="busy"
      :secondary-label="bar.secondaryLabel ?? undefined"
      :wide="wide"
      @back="back"
      @next="onNext"
      @secondary="onSecondary"
    />
  </div>
</template>

<style scoped lang="scss">
  // Workspace archetype: the page's own top and bottom bars, a scrolling body between them.
  .reservations-guide {
    @apply tw-flex tw-h-full tw-min-h-0 tw-min-w-0 tw-flex-col tw-bg-canvas;
    .guide-scroll {
      @apply tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-px-4 tw-py-4 md:tw-px-6;
      // The floor plan sizes its cells from this width: keep the gutter stable.
      scrollbar-gutter: stable;
    }
    // Step then preview, in one column; the wide tier is a class from the
    // stable window size (never a media query), so a keyboard resize cannot flip it.
    .guide-content {
      @apply tw-mx-auto tw-flex tw-w-full tw-max-w-[44rem] tw-flex-col tw-gap-6;
    }
    .wide .guide-content {
      @apply tw-grid tw-max-w-[67.5rem] tw-items-start;
      grid-template-columns: minmax(0, 1fr) 22rem;
    }
    .guide-step {
      @apply tw-min-w-0 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-4 md:tw-p-6;
    }
    .guide-loading {
      @apply tw-mx-auto tw-flex tw-w-full tw-max-w-[44rem] tw-flex-col tw-gap-3;
      .skeleton-title {
        @apply tw-h-7 tw-w-2/3;
      }
      .skeleton-row {
        @apply tw-h-20 tw-w-full;
      }
    }
  }
</style>
